import { OpenFeature } from '@openfeature/react-sdk';

import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';

/**
 * Kill switch for running the backup FFmpeg mux detached on the desktop stop
 * path.
 *
 * Default OFF — the stop path keeps awaiting the backup mux inline, i.e.
 * today's behaviour. ON, the stop pipeline hands the mux off and returns
 * without waiting for the encode, so a long WAV no longer holds the user on a
 * spinner after they hit stop.
 *
 * Scope: ON, this switch detaches for **any** recording, including a pause. A
 * recording locked at start as transcription-service V2 also requests the
 * detach without this flag — its chunk transcript is the source note
 * generation reads and the MP3 is redundancy only — but that V2 rule applies
 * only to a note-generating stop and **never** to a pause, so the single
 * detached-mux slot stays free for the stop that actually gates note
 * generation. See `shouldDetachBackupFromStop` in
 * `rustChunkUploadPauseFinalization.ts`. Rust still owns the final decision
 * either way.
 *
 * Deliberately INDEPENDENT of `release-apps-self-healing-stop-desktop`: that
 * flag's payload also drops the tolerated failed-chunk count from 3 to 0, which
 * is not part of this rollout. One flag, one behaviour.
 *
 * This is a rollout/kill gate, not an experiment — do not add
 * `useManualExposure`.
 *
 * Non-hook only: the sole reader is the stop mutation's async `mutationFn`,
 * which cannot call a React hook. `OpenFeature.getClient(...)` resolves against
 * the default NoOp evaluation outside a `<OpenFeatureProvider>`, so this is
 * safe with no provider mounted (e.g. the stop-mutation unit tests).
 */
export const DETACHED_BACKUP_MUX_FLAG_KEY = 'release-desktop-detached-backup-mux';

export const isDetachedBackupMuxEnabled = (): boolean => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    DETACHED_BACKUP_MUX_FLAG_KEY,
    false
  );
};
